import { styled } from "@mui/material/styles";
import {
  DataGrid,
  gridClasses,
  DataGridProps,
  GridColDef,
  GridRowsProp,
} from "@mui/x-data-grid";
import { TABLE_COLORS, TABLE_FONT } from "../../constants";
import { PageSizeOptions, ROW_HEIGHT } from "../../constants/productUploads";

type TableProps = DataGridProps & {
  disablePositionColumn?: boolean;
  positionColumnOptions?: Partial<Omit<GridColDef, "cellClassName">>;
  getRowPosition?: (index: number) => number;
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}`]: {
    backgroundColor: TABLE_COLORS.rowBackground,
    "&:hover": {
      backgroundColor: TABLE_COLORS.rowHover,
    },
  },
  [`& .${gridClasses.cell}`]: {
    fontWeight: 500,
    cursor: "pointer",
  },
  "& .MuiTablePagination-root p": {
    margin: 0,
  },
  [`& .${gridClasses.columnHeader}`]: {
    backgroundColor: "transparent",
  },
  [`&.MuiDataGrid-root .MuiDataGrid-container--top [role=row]`]: {
    backgroundColor: TABLE_COLORS.columnHeader,
  },
  // Custom styles for the position column, use this class in your columns definition
  "& .position-column": {
    borderRight: `1px solid ${theme.palette.divider}`,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: TABLE_COLORS.columnHeader,
  },
  "& *": {
    fontFamily: TABLE_FONT,
  },
}));

const PositionColumnDefaultOptions: GridColDef = {
  field: "position",
  maxWidth: 70,
  headerName: "",
  align: "right",
  cellClassName: "position-column",
  disableColumnMenu: true,
  sortable: false,
};

export const Table = ({
  disablePositionColumn,
  positionColumnOptions,
  getRowPosition,
  columns,
  rows,
  rowHeight = ROW_HEIGHT,
  pageSizeOptions = PageSizeOptions,
  ...restProps
}: TableProps) => {
  const rowsWithPosition: GridRowsProp = (rows ?? []).map((row, index) => ({
    position: getRowPosition?.(index) ?? index + 1,
    ...row,
  }));

  const defaultColumns: GridColDef[] = [
    {
      ...PositionColumnDefaultOptions,
      ...positionColumnOptions,
    },
  ];

  return (
    <StyledDataGrid
      disableRowSelectionOnClick
      pageSizeOptions={pageSizeOptions}
      rowHeight={rowHeight}
      rows={disablePositionColumn ? rows : rowsWithPosition}
      columns={
        disablePositionColumn ? columns : [...defaultColumns, ...columns]
      }
      {...restProps}
    />
  );
};

export default Table;
